import { UserRole, OPERATIONS_ROLES, canOperate, isAdminRole } from '@bhops/shared';

// 화면 권한 헬퍼 — **판정은 공용 정본 한 벌**(packages/shared/src/roles.ts).
//
// ── 권한 통합 (오너 결정 2026-08-27) ────────────────────────────────────
//   "사업팀, 제조팀, 필드팀 권한을 구분하지 않고 모든 등록과 삭제, 완전삭제는
//    오픈하겠음. 권한이 없어서 보지 못했던 탭도 오픈. 다만 로그로 누가 삭제하고
//    등록하고 수정했는지 명확하게 기록이 남을 수 있어야함.
//    // Admin 탭은 여전히 관리자만 볼 수 있도록."
//
// 종전 이 파일에는 팀별 헬퍼가 여럿 있었다(canEditSales / canEditMfg / canEditField
// / canDeployRobot / canCreateStore / canSwapRobotParts / canDeleteRobot /
// canCorrectRobotStatus / canCorrectToRetirement / canEditEcn / canHardDeleteCatalog
// / canEditAny). 각각이 **서버의 어떤 규칙 한 자리의 거울**이었고, 그 거울을 손으로
// 맞추는 것이 이 저장소가 반복해서 겪은 사고의 원인이었다
// ("서버는 되는데 화면이 없음" · "버튼은 보이는데 저장만 403").
//
// 이제 갈래가 없으므로 헬퍼도 하나다. 이름은 남겨 둔다 — 호출부가 수백 곳이고,
// 이름을 지우면 그 자리마다 "무엇을 판정하는 것이었나"를 다시 읽어야 한다.
// 전부 같은 판정(canOperate)에 위임하고, **뜻이 다른 두 개만** 따로 둔다:
//   · canSeePrice          — 금액 열람. 오너가 말한 축(등록·삭제·탭)이 아니다.
//   · canHardDeleteCatalog — 이름과 달리 이제 관리자 전용이 아니다(아래 참조).
//
// ⚠ 새 게이트를 만들 때 여기에 헬퍼를 **추가하지 마라.** canOperate 를 직접 쓴다.
//   헬퍼가 늘면 갈래가 다시 생긴다.

/**
 * 금액·매출 열람 가능 여부(§D-3 금액 가리기).
 *
 * **권한 통합에서 일부러 넓히지 않았다.** 오너가 없앤 것은 "누가 등록·삭제·수정하는가"
 * 와 "어떤 탭이 보이는가"이고, 금액 열람은 다른 축이다. 넓히려면 별도 결정이 필요하다.
 */
export function canSeePrice(role: UserRole): boolean {
  return role === UserRole.ADMIN || role === UserRole.SALES_MANAGER || role === UserRole.MANAGER;
}

/** 업무 자원 편집 가능 여부 — 아래 모든 별칭이 같은 이 판정을 쓴다. */
export function canEditAny(role: UserRole): boolean {
  return canOperate(role);
}

/** @deprecated 팀 구분이 사라졌다(2026-08-27). canEditAny 와 같다. */
export const canEditSales = canEditAny;
/** @deprecated 팀 구분이 사라졌다(2026-08-27). canEditAny 와 같다. */
export const canEditMfg = canEditAny;
/** @deprecated 팀 구분이 사라졌다(2026-08-27). canEditAny 와 같다. */
export const canEditField = canEditAny;
/** @deprecated 팀 구분이 사라졌다(2026-08-27). canEditAny 와 같다. */
export const canDeployRobot = canEditAny;
/** @deprecated 팀 구분이 사라졌다(2026-08-27). canEditAny 와 같다. */
export const canCreateStore = canEditAny;
/** @deprecated 팀 구분이 사라졌다(2026-08-27). canEditAny 와 같다. */
export const canSwapRobotParts = canEditAny;
/** @deprecated 팀 구분이 사라졌다(2026-08-27). canEditAny 와 같다. */
export const canDeleteRobot = canEditAny;
/** @deprecated 팀 구분이 사라졌다(2026-08-27). canEditAny 와 같다. */
export const canCorrectRobotStatus = canEditAny;
/** @deprecated 팀 구분이 사라졌다(2026-08-27). canEditAny 와 같다. */
export const canEditEcn = canEditAny;

/**
 * 폐기 계열(폐기 예정/폐기 완료)로의 상태 정정.
 *
 * 종전엔 ADMIN 전용이었다(되돌릴 수 없는 자산 상각). **오너가 회계·법적 4종을 포함해
 * 전부 열기로 결정**했고(엔지니어링 권고는 관리자 유지였다), 서버의 전용 가드는
 * CASL 과 조건이 같아져 닿을 수 없는 코드가 되어 삭제됐다.
 * 안전망은 감사 장부다 — robot.status-correction 이 10자 이상 사유와 값 diff 를 남긴다.
 */
export const canCorrectToRetirement = canEditAny;

/**
 * 카탈로그(변경점·개조) 완전삭제.
 *
 * 이름이 "hardDelete"라 관리자 전용처럼 읽히지만 **더 이상 아니다** — 위와 같은
 * 결정으로 운영 권한에 열렸다(서버 ecns/loader-revs service 의 같은 개방과 짝).
 */
export const canHardDeleteCatalog = canEditAny;

/**
 * 부품 카탈로그·부품 입고·LOT 영향권·공급사 화면의 접근 역할.
 *
 * 오너 요구("권한이 없어서 보지 못했던 탭도 오픈")에 따라 **운영 권한 전체**로 열었다.
 * 종전엔 [ADMIN, MANAGER, MFG_MANAGER] 였다. router.tsx 의 requiredRoles 와 Sidebar 의
 * allowedRoles 가 이 상수 하나를 공유한다.
 *
 * ⚠ 2026-08-28 — 오너가 **사이드바 세 항목(부품 카탈로그·부품 입고·공급사)을 전원에게
 *   숨기라고** 결정했다. 그 숨김은 `layout/nav-hidden.ts` 에 있고, **이 상수는 일부러
 *   그대로 둔다.** 두 가지를 혼동하면 안 된다:
 *     · 권한(이 상수) — 들어갈 수 있는 역할. 바뀌지 않았다.
 *     · 메뉴 노출(nav-hidden.ts) — 입구를 보여줄지. 이번에 닫혔다.
 *
 *   이 상수를 좁히거나 비워서 숨기려 하면 두 가지가 망가진다:
 *     ① 이 상수는 **LOT 영향권(`parts/lots/:id`)까지 네 화면**을 덮는다. 그 화면은
 *        사이드바에 아예 없고 로봇 상세 '부품' 탭의 LOT 링크로만 들어가며, LOT
 *        Recall·이상 마킹·정상 처리(CLEARED) 버튼이 거기에만 있다. 오너가 숨기라고
 *        한 것에 그 화면은 없다.
 *     ② `roleCanAccessRoute` 는 빈 목록을 **전원 허용**으로 읽는다 — 비우면 숨김이
 *        아니라 정반대가 된다(VIEWER 에게까지 노출).
 */
export const PARTS_SUPPLIERS_ROUTE_ROLES: ReadonlyArray<UserRole> = OPERATIONS_ROLES;

/**
 * '회수 검토'(폐기 후보 조회) 화면의 접근 역할. 조회 전용 화면이다.
 * 종전 [ADMIN, MANAGER, MFG_MANAGER] → 운영 권한 전체.
 */
export const RETIRE_CANDIDATES_ROUTE_ROLES: ReadonlyArray<UserRole> = OPERATIONS_ROLES;

/**
 * 사이드바 항목 가시성 판정 — ProtectedRoute 와 **동일 규칙**으로 라우트 접근 가능
 * 여부를 반환한다(매핑 표류 방지: router 의 requiredRole(s) 와 같은 값을 사이드바
 * allowedRoles 에 미러링하고 이 헬퍼로 필터).
 *   - allowedRoles 미지정/빈 배열 = 전원 허용(현행 동작 보존)
 *   - ADMIN 은 super-role 로 항상 통과(ProtectedRoute L37 과 일치)
 *   - 그 외에는 role 이 allowedRoles 에 포함될 때만 true
 * ⚠ 숨김은 UX 전용 — 실제 접근 차단은 ProtectedRoute/서버 API 가 담당한다.
 */
export function roleCanAccessRoute(
  role: UserRole,
  allowedRoles?: ReadonlyArray<UserRole>,
): boolean {
  if (!allowedRoles || allowedRoles.length === 0) return true;
  if (isAdminRole(role)) return true;
  return allowedRoles.includes(role);
}

/** 사업/제조/현장 그룹의 사이드바 노출 — 읽기만 되면 보인다(VIEWER 포함). */
export function canSeeSalesGroup(role: UserRole): boolean {
  return canOperate(role) || role === UserRole.VIEWER;
}

/** 제조 그룹의 사이드바 노출. */
export const canSeeMfgGroup = canSeeSalesGroup;

/** 현장 그룹의 사이드바 노출. */
export const canSeeFieldGroup = canSeeSalesGroup;
